import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useLang } from '../i18n';
import { INSTAGRAM_URL, LOGO, RETAILER_URL } from '../lib/api';
import { withUtm } from '../lib/utm';
import { MonogramPattern } from './Brush';
import { LangSwitch } from './Header';

/**
 * La firma che chiude ogni pagina: il payoff a tutta larghezza, in corsivo,
 * posata sul bordo inferiore dello schermo. Sale mentre si arriva in fondo,
 * come una firma che si posa sul foglio. Decorativa (il payoff e' gia' nel
 * logo e nel titolo della home): fuori dall'albero di accessibilita'.
 */
const Firma = () => {
  const ref = useRef(null);
  const ridotta = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end end'] });
  const y = useTransform(scrollYProgress, [0, 1], ['60%', '0%']);
  return (
    <div ref={ref} aria-hidden className="relative overflow-hidden">
      <motion.p
        style={{ y: ridotta ? '0%' : y }}
        className="accento select-none whitespace-nowrap text-center text-[clamp(3rem,12.6vw,13.5rem)] leading-[0.86] tracking-[-0.02em] text-carta"
      >
        Vivi di emozioni
      </motion.p>
    </div>
  );
};

export const Footer = () => {
  const { t, lang, setLang } = useLang();
  const year = new Date().getFullYear();

  // Tre colonne sui tre hub, non piu' un elenco unico "per la tua casa" da
  // otto voci. Le etichette riusano i titoli delle pagine dove esistono.
  const cols = [
    {
      title: t('footer.colCatalog'),
      links: [
        ['/catalogo', t('catalog.title')],
        ['/collezioni', t('nav.collections')],
        ['/componi', t('nav.build')],
        ['/dove-comprare', t('nav.whereToBuy')],
      ],
    },
    {
      title: t('footer.colStories'),
      links: [
        ['/storie', t('stories.title')],
        ['/storie/shades', t('nav.shades')],
        ['/storie/articoli', t('nav.magazine')],
        ['/stagioni/natale', t('nav.xmas')],
        ['/azienda', t('nav.company')],
      ],
    },
    {
      title: t('footer.colPro'),
      links: [
        ['/professionisti/rivenditori', t('nav.reseller')],
        ['/professionisti/horeca', t('nav.horeca')],
        ['/area-stampa', t('nav.press')],
        ['/contatti', t('nav.contact')],
      ],
    },
    {
      title: t('footer.colLegal'),
      links: [
        ['/informativa-privacy', 'Informativa privacy'],
        ['/cookie-policy', 'Cookie policy'],
        ['/note-legali', 'Note legali'],
        ['/accessibilita', 'Accessibilità'],
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="relative mt-24 overflow-hidden bg-inchiostro text-carta">
      <MonogramPattern className="absolute inset-0 pointer-events-none" opacity={0.06} />

      <div className="wrap relative pb-10 pt-16 md:pb-14 md:pt-24">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div>
            <img src={LOGO.minimalLight} alt="VdE Tivoli 1996, 30 anni" className="mb-6 h-14 w-auto" width="511" height="151" />
            {/* Due cluster distinti: chi compra (Satur, Amazon) e chi rivende
                (Faire, Diventa rivenditore). Faire e' B2B (AD-5), non sta coi
                canali B2C. Erano precedute da "Questo sito racconta. Non vende.":
                i due gruppi di canali rispondono alla stessa domanda facendo,
                invece di spiegare il sito a chi lo sta usando. */}
            <div className="grid gap-6">
              <div data-testid="footer-b2c">
                <p className="mb-3 text-[14px] font-bold text-carta/70">
                  {t('footer.channelsB2C')}
                </p>
                <a
                  href={withUtm(RETAILER_URL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-retailer-link"
                  className="link-ul flex min-h-[44px] items-center text-[16px] font-semibold text-ocra transition-colors hover:text-ocra"
                >
                  satur.it · {t('common.galileoStore')}
                </a>
                <a
                  href="https://www.amazon.it/stores/VDETIVOLI1996/page/714F2946-9878-45A4-8865-33C2D9A24888?lp_asin=B0DXVPFC16&ref_=ast_bln&store_ref=bl_ast_dp_brandlogo_sto"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-amazon-link"
                  className="link-ul flex min-h-[44px] items-center text-[16px] font-semibold text-carta/70 transition-colors hover:text-ocra"
                >
                  Amazon store
                </a>
              </div>

              <div data-testid="footer-b2b">
                <p className="mb-3 text-[14px] font-bold text-carta/70">
                  {t('footer.channelsB2B')}
                </p>
                <a
                  href="https://www.faire.com/brand/b_xczuwh5rxj"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-faire-link"
                  className="link-ul flex min-h-[44px] items-center text-[16px] font-semibold text-carta/70 transition-colors hover:text-ocra"
                >
                  Faire wholesale
                </a>
              </div>

              {/* feedback 02/11/2026: mancavano i rimandi ai social */}
              <div data-testid="footer-social">
                <p className="mb-3 text-[14px] font-bold text-carta/70">
                  {t('footer.follow')}
                </p>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="link-ul flex min-h-[44px] items-center text-[16px] font-semibold text-carta/70 transition-colors hover:text-ocra"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>

          {cols.map((c) => (
            <nav key={c.title}>
              <p className="mb-5 text-[14px] font-bold text-carta/70">
                {c.title}
              </p>
              <ul>
                {c.links.map(([to, label]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      data-testid={`footer-link-${to.replace(/^\//, '').replace(/\//g, '-')}`}
                      className="link-ul flex min-h-[44px] items-center text-[16px] text-carta/85 transition-colors hover:text-ocra"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-16 flex flex-col gap-4 text-[14px] text-carta/70 md:flex-row md:items-center md:justify-between">
          <span>© {year} VdE Tivoli 1996 · Galileo S.p.A. · P.IVA 08442821008</span>
          <div className="flex items-center gap-2">
            <span>{t('common.langLabel')}</span>
            {/* stesso componente dell'header invece di una copia inline */}
            <LangSwitch lang={lang} setLang={setLang} light testid="lang-switch-footer" />
          </div>
        </div>
      </div>
      <Firma />
    </footer>
  );
};
